import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { fetchPublicationByIssueYearGroups, fetchPublicationByIssueList } from "../../../../services/publicationSearchService";
import { Link } from "react-router-dom";
import { buildPublicationPath } from "../../../../utils/slugify";
import Spinner from "react-bootstrap/Spinner";
import { ChevronDown } from "react-feather";

const CHUNK = 5;

/* UTILS - Yardımcı fonksiyonlar */
const getAttr = (issue, name) =>
  issue?.publicationtypeInfo?.find(a => a?.attributename === name)?.value?.[0]?.value?.[0] ??
  issue?.templateInfo?.find(a => a?.attributename === name)?.value?.[0]?.value?.[0] ?? null;

// parseStructuredDate ve getIssueDate fonksiyonlarını sıralama için kullanacağız
const parseStructuredDate = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s.replace(" ", "T"));
    return isNaN(d) ? null : d;
  }
  const dmY = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dmY) {
    const [, dd, mm, yyyy] = dmY.map(Number);
    const d = new Date(yyyy, mm - 1, dd);
    return isNaN(d) ? null : d;
  }
  return null;
};

const getAttrValue = (arr = [], name) => {
  const row = arr?.find(a => a?.attributename === name);
  const v = row?.value?.[0]?.value?.[0];
  return (v === null || v === undefined) ? "" : String(v).trim();
};

const getIssueDate = (issue) => {
  if (!issue) return null;
  let d = parseStructuredDate(getAttrValue(issue.publicationtypeInfo, "Yayın Tarihi"));
  if (d) return d;
  d = parseStructuredDate(getAttrValue(issue.templateInfo, "Yayın Tarihi"));
  if (d) return d;
  d = parseStructuredDate(getAttrValue(issue.templateInfo, "Erişim Tarihi"));
  if (d) return d;
  // Yedek olarak Cilt/Sayı tahmini
  const year = Number(getAttr(issue, "Cilt")) || null;
  const no = Number(getAttr(issue, "Sayı")) || null;
  if (year) {
    const week = Math.max(1, no || 1);
    const approx = new Date(year, 0, 1 + (week - 1) * 7);
    return isNaN(approx) ? null : approx;
  }
  return null;
};

const sortIssuesByDateAsc = (issues = []) =>
  [...issues].sort((a, b) => {
    const ta = getIssueDate(a)?.getTime() ?? -Infinity;
    const tb = getIssueDate(b)?.getTime() ?? -Infinity;
    if (ta !== tb) return ta - tb;
    return (a.mysqlId ?? -Infinity) - (b.mysqlId ?? -Infinity);
  });

const sortIssuesByDateDesc = (issues = []) => sortIssuesByDateAsc(issues).reverse();

const PreviousIssues = ({ publicationId, publicationType }) => {
  // --- STATE ---
  const [years, setYears] = useState([]); // Tüm yıl listesi (String olarak: ["2025", "2024"...])
  const [displayCount, setDisplayCount] = useState(CHUNK);
  const [yearIssueMap, setYearIssueMap] = useState({}); // { "2025": [Issue, Issue...], "2024": [...] }

  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadedYears, setLoadedYears] = useState(() => new Set());
  const [selectedYear, setSelectedYear] = useState("");

  // 1) YIL LİSTESİNİ ÇEK
  useEffect(() => {
    let alive = true;
    const loadYears = async () => {
      try {
        setLoadingYears(true);
        const yearsRes = await fetchPublicationByIssueYearGroups(publicationId, publicationType, 1);
        if (!alive) return;
        if (yearsRes?.status === "OK" && Array.isArray(yearsRes.data)) {
          // Gelen yılları String formatında, büyükten küçüğe sıralı sakla
          const list = yearsRes.data
            .map(y => String(y.key).trim())
            .filter(y => /^\d+$/.test(y))
            .sort((a, b) => Number(b) - Number(a));
          setYears(list);
        } else {
          setYears([]);
        }
      } catch {
        setYears([]);
      } finally {
        if (alive) setLoadingYears(false);
      }
    };
    loadYears();
    return () => { alive = false; };
  }, [publicationId, publicationType]);

  // Yardımcı: Belirli bir yılı API'den çekip Map'e atar
  const fetchYearData = async (yearStr) => {
    // API genellikle integer yıl bekler, güvenli parse
    const res = await fetchPublicationByIssueList(publicationId, publicationType, parseInt(yearStr, 10), 1);
    const arr = (res?.status === "OK" && Array.isArray(res.data)) ? res.data : [];
    return sortIssuesByDateDesc(arr);
  };

  // 2) Dropdown'dan yıl seçilince çalışır
  const ensureYearLoaded = async (yearStr) => {
    if (!yearStr || loadedYears.has(yearStr)) return;
    setLoadingMore(true);
    try {
      const sortedData = await fetchYearData(yearStr);
      setYearIssueMap(prev => ({ ...prev, [yearStr]: sortedData }));
      setLoadedYears(prev => {
        const next = new Set(prev);
        next.add(yearStr);
        return next;
      });
    } finally {
      setLoadingMore(false);
    }
  };

  // 3) "Daha Fazla Göster" ile toplu yükleme
  const loadMoreYears = async (from, to) => {
    const slice = years.slice(from, to).filter(y => !loadedYears.has(y));
    if (slice.length === 0) return;

    setLoadingMore(true);
    try {
      const results = await Promise.all(slice.map(y => fetchYearData(y)));

      const newMapData = {};
      results.forEach((data, idx) => {
        const yStr = slice[idx];
        newMapData[yStr] = data;
      });

      setYearIssueMap(prev => ({ ...prev, ...newMapData }));
      setLoadedYears(prev => {
        const next = new Set(prev);
        slice.forEach(y => next.add(y));
        return next;
      });
    } finally {
      setLoadingMore(false);
    }
  };

  // İlk açılışta ilk CHUNK kadar yılı yükle
  useEffect(() => {
    if (years.length > 0 && loadedYears.size === 0) {
      loadMoreYears(0, Math.min(CHUNK, years.length));
    }
  }, [years]);

  // BUTON TIKLAMASI
  const handleLoadMore = () => {
    const prev = displayCount;
    const next = Math.min(displayCount + CHUNK, years.length);
    setDisplayCount(next);
    loadMoreYears(prev, next);
  };

  const handleYearChange = async (e) => {
    const val = e.target.value;
    setSelectedYear(val);
    if (val) await ensureYearLoaded(val);
  };

  // Hangi yılları göstereceğiz?
  const visibleYears = useMemo(() => {
    if (selectedYear) return [selectedYear]; // Sadece seçili yıl
    return years.slice(0, displayCount);     // Veya yüklenen tüm yıllar
  }, [selectedYear, years, displayCount]);

  // Toplam sayı adedi (Header için)
  const totalVisibleIssues = useMemo(() => {
    // 1. DURUM: Yıl seçili -> Adet dön
    if (selectedYear) {
      return yearIssueMap[selectedYear]?.length || 0;
    }

    // 2. DURUM: Tümü -> En güncel yılın en güncel sayı numarasını dön
    if (years.length > 0) {
      const newestYear = years[0];
      const issues = yearIssueMap[newestYear];
      if (issues && issues.length > 0) {
        return getAttr(issues[0], "Sayı");
      }
    }
    return null;
  }, [selectedYear, years, yearIssueMap]);

  const showMoreVisible = !selectedYear && (displayCount < years.length);

  return (
    <div className="mt-4">
      {/* FILTER BAR */}
      <div
        className="d-flex flex-wrap justify-content-between align-items-center mb-3 p-2 border rounded-3 shadow-sm"
        style={{ gap: 8, background: "#f8fafc" }}
      >
        <div className="d-flex align-items-center gap-2">
          <span className="d-inline-flex align-items-center text-fume fs-6">Yıl:</span>
          <div className="input-group-sm" style={{ width: 240 }}>
            <select
              className="form-select"
              value={selectedYear}
              onChange={handleYearChange}
              aria-label="Yıl filtresi"
            >
              <option value="">Tümü</option>
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="text-fume small">
          {selectedYear ? (
            <span>{selectedYear} • {totalVisibleIssues} sayı</span>
          ) : (
            <span>Tümü</span>
          )}
        </div>
      </div>

      {(loadingYears && loadedYears.size === 0) ? (
        <div className="d-flex align-items-center my-3">
          <Spinner animation="border" size="sm" className="text-info me-2" />
          <span>Yükleniyor...</span>
        </div>
      ) : (
        <div>
          {visibleYears.length > 0 && totalVisibleIssues === 0 && loadedYears.size > 0 && (
            <div className="text-fume">Bu kriterlere uygun sayı bulunamadı.</div>
          )}

          <hr className="my-3 opacity-50" style={{ borderColor: "#b0b3b8" }} />

          {/* DOĞRUDAN YILLARI LİSTELİYORUZ */}
          {visibleYears.map(yearStr => {
            const issues = yearIssueMap[yearStr] || [];
            if (issues.length === 0) return null; // Verisi gelmemiş yılı boş gösterme

            // İsteğe bağlı: Eğer o yılda "Cilt" bilgisi varsa başlığa ekleyebiliriz.
            // Genelde bir yılda tek cilt olur ama istisnalar olabilir.
            // Basitlik adına Başlık olarak YIL kullanıyoruz.
            const sampleCilt = getAttr(issues[0], "Cilt");
            const headerTitle = (sampleCilt && sampleCilt !== "-")
              ? `${yearStr} (Cilt: ${sampleCilt})`
              : `${yearStr}`;

            return (
              <div key={yearStr} className="mb-4 fade-in-up">
                {/* Yıl Başlığı */}
                <div className="text-fume mb-2 pb-1 border-bottom" style={{ fontSize: "1.1rem", fontWeight: 600, color: "#333" }}>
                  {headerTitle}
                </div>

                {issues.map((issue, index) => {
                  const sayi = getAttr(issue, "Sayı") || "-";
                  const baslik = getAttr(issue, "Başlık") || "Başlık Yok";
                  const uniqueKey = issue.mysqlId ? `${issue.mysqlId}-${index}` : `${yearStr}-${index}`;

                  return (
                    <div key={uniqueKey} className="text-fume mx-2 my-1" style={{ fontSize: "0.95rem", lineHeight: 1.6 }}>
                      <span className="" style={{ minWidth: "60px", display: "inline-block" }}>Sayı: {sayi}</span>
                      <span className="mx-2 text-muted">|</span>
                      <Link
                        to={buildPublicationPath({ title: baslik, id: issue.mysqlId })}
                        className="text-blue hover-underline"
                      >
                        {baslik}
                      </Link>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {showMoreVisible && (
            <div className="text-center my-4">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                onClick={handleLoadMore}
                disabled={loadingMore}
                style={{ minWidth: 160, justifyContent: "center" }}
              >
                {loadingMore ? (
                  <>
                    <Spinner size="sm" animation="border" /> Yükleniyor...
                  </>
                ) : (
                  <>
                    <ChevronDown size={16} /> Daha Fazla Göster
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

PreviousIssues.propTypes = {
  publicationId: PropTypes.string.isRequired,
  publicationType: PropTypes.string.isRequired,
};

export default PreviousIssues;